import api from '@disco/api/server'
import { BUSINESS_DATA_LOAD } from '@disco/constants/ActionTypes'

import React, { useState } from 'react'
import { useDispatch, useSelector } from 'hooks/use-redux'
import { useNavigate, useLocation } from 'react-router-dom'
import type { Location } from 'react-router-dom'
import { sendGenericError } from '@disco/state-management/actions/error-actions'
import { getArtistPlanSignupSent } from '@disco/state-management/selectors/user'
import { parseServerError } from '@disco/utils/captureError'
import { getRootPath } from '@disco/utils/pricing'
import Row from 'components/common/row'
import {
  artistProjectOptions,
  getVerificationOptions,
  verificationOptions,
} from 'components/modals/artist-verification-modal/options'
import ArtistVerificationSuccessModal from 'components/modals/artist-verification-modal/success'
import Alert from 'components/ui/alert'
import Button from 'components/ui/button'
import ButtonContainer from 'components/ui/button-container'
import DiscoModal from 'components/ui/disco-modal'
import CustomDropdown from 'components/ui/dropdowns/custom-dropdown'
import Heading from 'components/ui/heading'
import Label from 'components/ui/label'
import Stack from 'components/ui/stack'
import TextField from 'components/ui/text-field'
import type { BusinessData } from 'types/business'

const ArtistVerificationModal = (): React.ReactNode => {
  const location = useLocation()
  const navigate = useNavigate()
  const rootPath = getRootPath(location)
  const dispatch = useDispatch()
  const signupSent = useSelector(getArtistPlanSignupSent)
  const business = useSelector(
    state => state.user?.business || ({} as BusinessData),
  )
  const [formState, setFormState] = useState<Record<string, any>>({
    business_user_job: null,
  })
  const [verState, setVerState] = useState({
    verOne: verificationOptions[0],
    verTwo: verificationOptions[1],
  })
  const [loading, setLoading] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [validationError, setValidationError] = useState<
    Record<string, any> | null | undefined
  >(null)

  const verOne = verState.verOne
  const verTwo = verState.verTwo

  if (!verOne || !verTwo) return null

  const updateForm = (fieldName: string, value?: string) => {
    if (verOne.value === fieldName) {
      setValidationError({ ...validationError, verOne: null })
    } else {
      setValidationError({ ...validationError, verTwo: null })
    }

    setFormState({ ...formState, [fieldName]: value || '' })
  }

  const updateOptions = (fieldName: string, value: Record<string, any>) => {
    setValidationError({ ...validationError, [fieldName]: null })
    setVerState({ ...verState, [fieldName]: value || null })
  }

  const onSubmit = async e => {
    e.preventDefault()
    setLoading(true)
    setSubmitError(null)
    setValidationError(null)

    try {
      await api.artistSignup(formState)
      dispatch({
        type: BUSINESS_DATA_LOAD,
        result: {
          ...business,
          private_metadata: {
            ...business.private_metadata,
            artist_plan_signup_sent: true,
          },
        },
      })
    } catch (error) {
      if (error?.status === 400) {
        setValidationError({
          verOne: error?.body?.[verOne.value]?.[0],
          verTwo: error?.body?.[verTwo.value]?.[0],
        })
      } else {
        dispatch(sendGenericError(error))
        setSubmitError(
          parseServerError(error?.body?.[0] || error?.body || error),
        )
      }
    }

    setLoading(false)
  }

  const onClose = () => {
    navigate(rootPath)
  }

  const onInvalid = event => {
    event.preventDefault()
  }

  if (signupSent) {
    return <ArtistVerificationSuccessModal rootPath={rootPath} />
  }

  return (
    <DiscoModal isOpen showCloseButton onRequestClose={onClose}>
      <form onSubmit={onSubmit}>
        <Heading>Artist Verification</Heading>

        <Stack space="xsmall">
          <div className="form-note">
            Enter the information below to apply for our{' '}
            <a
              className="text-link"
              href="https://www.disco.ac/artist-plan-comparison"
              target="_blank"
              rel="noopener noreferrer"
            >
              artist plan
            </a>
            . Our team will review your submission and be in touch.
          </div>
          <div>
            <Label caption="Verification One" required />
            <Row columnsSize={[4, 8]} marginBottom={false}>
              <div>
                <CustomDropdown
                  options={getVerificationOptions(verTwo.value)}
                  value={verOne}
                  placeholder="Please select"
                  onChange={option => {
                    if (!option) return
                    updateForm(verOne.value)
                    updateOptions('verOne', option)
                  }}
                />
              </div>
              <div>
                <TextField
                  key={verOne.value}
                  name={verOne.value}
                  value={formState[verOne.value]}
                  onChange={event => {
                    updateForm(verOne.value, event?.target?.value)
                  }}
                  onInvalid={onInvalid}
                  placeholder={verOne.placeholder || verOne.label}
                  invalid={Boolean(validationError?.verOne)}
                  error={validationError?.verOne}
                  patternErrorMessage="This field is required"
                  required
                />
              </div>
            </Row>
          </div>
          <div>
            <Label caption="Verification Two" required />
            <Row columnsSize={[4, 8]} marginBottom={false}>
              <div>
                <CustomDropdown
                  options={getVerificationOptions(verOne.value)}
                  value={verTwo}
                  placeholder="Please select"
                  onChange={option => {
                    if (!option) return
                    updateForm(verTwo.value)
                    updateOptions('verTwo', option)
                  }}
                />
              </div>
              <div>
                <TextField
                  key={verTwo.value}
                  name={verTwo.value}
                  value={formState[verTwo.value]}
                  onChange={event => {
                    updateForm(verTwo.value, event?.target?.value)
                  }}
                  onInvalid={onInvalid}
                  placeholder={verTwo.placeholder || verTwo.label}
                  invalid={Boolean(validationError?.verTwo)}
                  error={validationError?.verTwo}
                  patternErrorMessage="This field is required"
                  required
                />
              </div>
            </Row>
          </div>

          <div>
            <Label
              caption="What best describes your artist project?"
              required
            />
            <CustomDropdown
              options={artistProjectOptions}
              value={formState.business_user_job}
              placeholder="Please select"
              onChange={option => {
                if (!option) return
                updateForm('business_user_job', option.value)
              }}
            />
            {/* TODO: add "invalid" design like in TextField for CustomDropdown*/}
            {/* https://disco-team.atlassian.net/browse/DISCO-2581 */}
            {!Boolean(formState.business_user_job) && (
              <TextField
                patternErrorMessage="This field is required"
                value=""
                onInvalid={onInvalid}
                required
                hidden
              />
            )}
          </div>
          {submitError && (
            <Alert status="danger" icon="warning">
              {submitError}
            </Alert>
          )}
          <ButtonContainer alignRight>
            <Button caption="Cancel" onClick={onClose} />
            <Button
              type="submit"
              appearance="primary"
              caption="Submit"
              progressCaption="Loading"
              progressIndicator={loading}
            />
          </ButtonContainer>
        </Stack>
      </form>
    </DiscoModal>
  )
}

export default ArtistVerificationModal
